import { inheritNativeDocumentImages } from '../public-format/native-images';
import { inheritNativeDocumentFonts } from '../public-format/native-fonts';
import { zipSync } from 'fflate';
import { inheritNativeDocumentText } from '../public-format/native-text';
import { releaseNativeDocumentResources } from '../public-format/native-resources';
import { artboards, type ExportAsset, type Slice } from '../core/export-resources';
import { createDocument, findLayer, layerDocumentTransform, type EditorDocument } from '../core/model';
import type { Renderer } from '../render/renderer';
import { downloadBlob } from './files';

export function sanitizeAssetFilename(name: string, fallback = 'asset'): string {
  return name.replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim() || fallback;
}

function drawingContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Export failed: canvas drawing context is unavailable.');
  return context;
}

function addArchiveEntry(entries: Record<string, Uint8Array>, base: string, bytes: Uint8Array): void {
  let name = base;
  for (let suffix = 2; Object.hasOwn(entries, name); suffix += 1) name = base.replace(/(\.[^.]+)$/, `-${suffix}$1`);
  entries[name] = bytes;
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Image encoding failed.')), type, quality));
}

function trimmedCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const image = drawingContext(source).getImageData(0, 0, source.width, source.height);
  let left = source.width; let top = source.height; let right = -1; let bottom = -1;
  for (let y = 0; y < source.height; y += 1) for (let x = 0; x < source.width; x += 1) {
    if ((image.data[(y * source.width + x) * 4 + 3] ?? 0) === 0) continue;
    left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y);
  }
  if (right < left || bottom < top) return source;
  const canvas = document.createElement('canvas');
  canvas.width = right - left + 1; canvas.height = bottom - top + 1;
  drawingContext(canvas).drawImage(source, left, top, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function assetDocument(document: EditorDocument, asset: ExportAsset): EditorDocument {
  if (asset.source.kind === 'document') return document;
  const source = findLayer(document.root, asset.source.layerId);
  if (!source) throw new Error(`Asset source “${asset.name}” no longer exists.`);
  const isolated = createDocument({ name: asset.name, width: document.width, height: document.height, background: 'transparent' });
  // This is a synchronous render projection, not a new authored document.
  // Keep identity-bound text and immutable layer content, while moving the
  // selected root into document coordinates. Ancestor appearance is excluded,
  // as before: a layer asset exports that layer's own appearance.
  isolated.id = document.id;
  isolated.extensions = document.extensions;
  const cloned = { ...source, transform: layerDocumentTransform(document.root, source.id) ?? source.transform };
  isolated.root.children = [cloned];
  isolated.activeLayerIds = [isolated.root.children[0]!.id];
  try {
    inheritNativeDocumentFonts(document, isolated);
    inheritNativeDocumentImages(document, isolated);
    inheritNativeDocumentText(document, isolated);
    return isolated;
  } catch (error) { releaseNativeDocumentResources(isolated); throw error; }
}

async function assetBytes(document: EditorDocument, renderer: Renderer, asset: ExportAsset): Promise<{ name: string; bytes: Uint8Array }> {
  const source = assetDocument(document, asset);
  let canvas: HTMLCanvasElement;
  try { canvas = renderer.flatten(source, asset.scale); }
  finally { if (source !== document) { try { renderer.disposeDocument(source); } finally { releaseNativeDocumentResources(source); } } }
  if (asset.trim) canvas = trimmedCanvas(canvas);
  const type = asset.format === 'png' ? 'image/png' : 'image/jpeg';
  if (asset.format === 'jpeg') {
    const opaque = globalThis.document.createElement('canvas');
    opaque.width = canvas.width; opaque.height = canvas.height;
    const context = drawingContext(opaque);
    context.fillStyle = '#fff'; context.fillRect(0, 0, opaque.width, opaque.height);
    context.drawImage(canvas, 0, 0);
    canvas = opaque;
  }
  const blob = await canvasBlob(canvas, type, asset.format === 'jpeg' ? 0.92 : undefined);
  const suffix = asset.scale === 1 ? '' : `@${asset.scale}x`;
  return { name: `${sanitizeAssetFilename(asset.name)}${suffix}.${asset.format === 'jpeg' ? 'jpg' : 'png'}`, bytes: new Uint8Array(await blob.arrayBuffer()) };
}

export async function exportAssetArchive(document: EditorDocument, renderer: Renderer, assets: readonly ExportAsset[], archiveName = 'maipo-assets'): Promise<number> {
  const entries: Record<string, Uint8Array> = {};
  for (const asset of assets) {
    const output = await assetBytes(document, renderer, asset);
    addArchiveEntry(entries, output.name, output.bytes);
  }
  downloadBlob(new Blob([zipSync(entries, { level: 0 })], { type: 'application/zip' }), `${sanitizeAssetFilename(archiveName)}.zip`);
  return assets.length;
}

export async function exportArtboardArchive(document: EditorDocument, renderer: Renderer): Promise<number> {
  const flattened = renderer.flatten(document);
  const entries: Record<string, Uint8Array> = {};
  for (const [index, artboard] of artboards(document).entries()) {
    const left = Math.max(0, Math.floor(artboard.rect.x));
    const top = Math.max(0, Math.floor(artboard.rect.y));
    const width = Math.max(1, Math.min(document.width - left, Math.round(artboard.rect.width)));
    const height = Math.max(1, Math.min(document.height - top, Math.round(artboard.rect.height)));
    const canvas = globalThis.document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const context = drawingContext(canvas);
    if (artboard.background === 'white') { context.fillStyle = '#fff'; context.fillRect(0, 0, width, height); }
    context.drawImage(flattened, left, top, width, height, 0, 0, width, height);
    const blob = await canvasBlob(canvas, 'image/png');
    addArchiveEntry(entries, `${sanitizeAssetFilename(artboard.name, `artboard-${index + 1}`)}.png`, new Uint8Array(await blob.arrayBuffer()));
  }
  downloadBlob(new Blob([zipSync(entries, { level: 0 })], { type: 'application/zip' }), `${sanitizeAssetFilename(document.name)}-artboards.zip`);
  return Object.keys(entries).length;
}

export async function exportSliceArchive(document: EditorDocument, renderer: Renderer, slices: readonly Slice[]): Promise<number> {
  const flattened = renderer.flatten(document);
  const entries: Record<string, Uint8Array> = {};
  for (const [index, slice] of slices.entries()) {
    const left = Math.max(0, Math.floor(slice.rect.x));
    const top = Math.max(0, Math.floor(slice.rect.y));
    const width = Math.max(1, Math.min(document.width - left, Math.round(slice.rect.width)));
    const height = Math.max(1, Math.min(document.height - top, Math.round(slice.rect.height)));
    const canvas = globalThis.document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const context = drawingContext(canvas);
    if (slice.format === 'jpeg') { context.fillStyle = '#fff'; context.fillRect(0, 0, width, height); }
    context.drawImage(flattened, left, top, width, height, 0, 0, width, height);
    const type = slice.format === 'jpeg' ? 'image/jpeg' : 'image/png';
    const blob = await canvasBlob(canvas, type, slice.format === 'jpeg' ? slice.quality / 100 : undefined);
    const extension = slice.format === 'jpeg' ? 'jpg' : 'png';
    const base = `${sanitizeAssetFilename(slice.name, `slice-${index + 1}`)}.${extension}`;
    addArchiveEntry(entries, base, new Uint8Array(await blob.arrayBuffer()));
  }
  downloadBlob(new Blob([zipSync(entries, { level: 0 })], { type: 'application/zip' }), `${sanitizeAssetFilename(document.name)}-slices.zip`);
  return Object.keys(entries).length;
}
